import React, { useEffect, useRef } from 'react'
import { Badge } from '@/components/ui/badge'
import { Calendar, Clock, User } from 'lucide-react'
import { format } from 'date-fns'
import DOMPurify from 'dompurify'

// TipTap node types
interface TipTapMark {
  type: string
  attrs?: Record<string, unknown>
}

interface TipTapNode {
  type?: string
  text?: string
  marks?: TipTapMark[]
  content?: TipTapNode[]
  attrs?: Record<string, unknown>
}

// Convert TipTap JSON to HTML
function tiptapJsonToHtml(jsonString: string): string {
  try {
    const doc = JSON.parse(jsonString) as TipTapNode
    return renderNode(doc)
  } catch (error) {
    console.error('Failed to parse TipTap JSON:', error)
    return jsonString // Fallback to original string if not JSON
  }
}

function renderNode(node: TipTapNode): string {
  if (!node) return ''

  if (node.type === 'text') {
    let text = node.text || ''
    if (node.marks) {
      node.marks.forEach((mark: TipTapMark) => {
        switch (mark.type) {
          case 'bold': text = `<strong>${text}</strong>`; break
          case 'italic': text = `<em>${text}</em>`; break
          case 'underline': text = `<u>${text}</u>`; break
          case 'strike': text = `<s>${text}</s>`; break
          case 'code': text = `<code>${text}</code>`; break
          case 'link': text = `<a href="${mark.attrs?.href || '#'}" target="${mark.attrs?.target || '_self'}">${text}</a>`; break
        }
      })
    }
    return text
  }

  const content = node.content ? node.content.map(renderNode).join('') : ''

  switch (node.type) {
    case 'doc': return content
    case 'paragraph': return `<p>${content}</p>`
    case 'heading': return `<h${node.attrs?.level || 1}>${content}</h${node.attrs?.level || 1}>`
    case 'bulletList': return `<ul>${content}</ul>`
    case 'orderedList': return `<ol start="${node.attrs?.start || 1}">${content}</ol>`
    case 'listItem': return `<li>${content}</li>`
    case 'blockquote': return `<blockquote>${content}</blockquote>`
    case 'codeBlock': return `<pre><code>${content}</code></pre>`
    case 'horizontalRule': return '<hr />'
    case 'hardBreak': return '<br />'
    case 'image': {
      const src = node.attrs?.src || ''
      const alt = node.attrs?.alt || 'Article image'
      return `<img src="${src}" alt="${alt}" class="blog-image" data-placeholder="true" />`
    }
    case 'table': return `<table>${content}</table>`
    case 'tableRow': return `<tr>${content}</tr>`
    case 'tableCell': return `<td>${content}</td>`
    case 'tableHeader': return `<th>${content}</th>`
    default: return content
  }
}

interface BlogPostPreviewProps {
  title: string
  content: string
  excerpt?: string
  featuredImage?: string
  categories: Array<{ id: string; name: string; color?: string }>
  tags: Array<{ id: string; name: string }>
  publishedAt: Date
  readingTime: number
  author?: string
}

export const BlogPostPreview: React.FC<BlogPostPreviewProps> = ({
  title,
  content,
  excerpt,
  featuredImage,
  categories,
  tags,
  publishedAt,
  readingTime,
  author = 'Admin',
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  // Convert TipTap JSON to HTML first
  const htmlContent = tiptapJsonToHtml(content)

  const sanitizedContent = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'table', 'thead',
      'tbody', 'tr', 'th', 'td', 'hr', 'div', 'span'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target', 'data-placeholder'],
  })

  // Handle broken images by replacing with placeholders
  useEffect(() => {
    if (!contentRef.current) return

    const images = contentRef.current.querySelectorAll('img.blog-image')
    images.forEach((img) => {
      const imgElement = img as HTMLImageElement

      imgElement.addEventListener('error', () => {
        const placeholder = document.createElement('div')
        placeholder.className = 'image-placeholder'
        placeholder.innerHTML = `
          <div class="flex flex-col items-center justify-center bg-muted rounded-lg p-8 my-6 min-h-[300px]">
            <svg class="w-16 h-16 text-muted-foreground mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <p class="text-sm text-muted-foreground">${imgElement.alt || 'Image placeholder'}</p>
          </div>
        `
        imgElement.parentNode?.replaceChild(placeholder.firstElementChild!, imgElement)
      })
    })
  }, [sanitizedContent])

  return (
    <div className="space-y-6">
      <div className="border border-border rounded-lg p-4 bg-muted/50">
        <h3 className="font-semibold mb-2">Preview Modes</h3>
        <div className="flex gap-2">
          <Badge variant="default">Desktop</Badge>
          <Badge variant="outline">Tablet</Badge>
          <Badge variant="outline">Mobile</Badge>
        </div>
      </div>

      <article className="max-w-4xl mx-auto bg-background">
        {featuredImage && (
          <div className="mb-8">
            <img
              src={featuredImage}
              alt={title}
              className="w-full h-96 object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          </div>
        )}

        <header className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.map((category) => (
              <Badge
                key={category.id}
                variant="default"
                style={category.color ? { backgroundColor: category.color } : {}}
              >
                {category.name}
              </Badge>
            ))}
          </div>

          <h1 className="text-4xl font-bold mb-4">{title || 'Untitled Post'}</h1>

          {excerpt && (
            <p className="text-xl text-muted-foreground mb-4">{excerpt}</p>
          )}

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{author}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{format(publishedAt, 'MMM dd, yyyy')}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{readingTime} min read</span>
            </div>
          </div>
        </header>

        <div
          ref={contentRef}
          className="prose prose-lg dark:prose-invert max-w-none mb-8
            prose-headings:font-bold prose-headings:tracking-tight
            prose-h1:text-5xl prose-h1:leading-tight prose-h1:mb-6 prose-h1:mt-12 first:prose-h1:mt-0
            prose-h2:text-4xl prose-h2:leading-snug prose-h2:mb-5 prose-h2:mt-12 prose-h2:border-b prose-h2:pb-2
            prose-h3:text-3xl prose-h3:leading-snug prose-h3:mb-4 prose-h3:mt-10
            prose-h4:text-2xl prose-h4:mb-3 prose-h4:mt-8
            prose-h5:text-xl prose-h5:mb-3 prose-h5:mt-6
            prose-h6:text-lg prose-h6:mb-2 prose-h6:mt-4
            prose-p:text-base prose-p:leading-relaxed prose-p:mb-4
            prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
            prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
            prose-li:mb-2 prose-li:leading-relaxed
            prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8 prose-img:w-full
            prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-6 prose-blockquote:pr-4 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:my-6 prose-blockquote:bg-muted/30 prose-blockquote:rounded-r
            prose-hr:my-12 prose-hr:border-t-2 prose-hr:border-border
            prose-table:w-full prose-table:my-8 prose-table:border-collapse prose-table:rounded-lg prose-table:overflow-hidden
            prose-thead:bg-muted
            prose-th:font-semibold prose-th:text-left prose-th:p-4 prose-th:border-b-2 prose-th:border-border
            prose-td:p-4 prose-td:border-t prose-td:border-border
            prose-tr:border-b prose-tr:border-border last:prose-tr:border-0
            prose-strong:font-bold prose-strong:text-foreground
            prose-em:italic
            prose-code:bg-muted prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
            prose-pre:bg-muted prose-pre:p-6 prose-pre:rounded-xl prose-pre:my-6 prose-pre:overflow-x-auto prose-pre:border prose-pre:border-border
            prose-a:text-primary prose-a:underline prose-a:decoration-primary/30 prose-a:underline-offset-2 hover:prose-a:decoration-primary prose-a:transition-colors"
          dangerouslySetInnerHTML={{ __html: sanitizedContent || '<p>No content yet...</p>' }}
        />

        {tags.length > 0 && (
          <footer className="border-t border-border pt-6">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Tags:</span>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge key={tag.id} variant="outline">
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          </footer>
        )}
      </article>

      <div className="border border-border rounded-lg p-4 bg-muted/50">
        <h3 className="font-semibold mb-4">SEO Preview</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">Google Search Result</h4>
            <div className="border border-border rounded p-3 bg-background">
              <div className="text-blue-600 dark:text-blue-400 text-lg hover:underline cursor-pointer">
                {title || 'Page Title'}
              </div>
              <div className="text-green-700 dark:text-green-400 text-sm">
                example.com › blog › {new Date().getFullYear()}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {excerpt || 'Page description will appear here...'}
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Social Media Card</h4>
            <div className="border border-border rounded overflow-hidden bg-background max-w-md">
              {featuredImage && (
                <img
                  src={featuredImage}
                  alt=""
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}
              <div className="p-3">
                <div className="font-semibold">{title || 'Post Title'}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {excerpt || 'Post description...'}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  example.com
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}